import React, { useState } from "react";
import { Check, CircleCheck, FileDown, FileText } from "lucide-react";

export const DEMO_SCENES = [
    { at: 0, label: "AuditVanguard", caption: "The compliance workspace for consultants and boutique GRC firms." },
    { at: 5.5, label: "Connect your frameworks", caption: "Select SOC 2, ISO 27001 or HIPAA — your control baseline is applied instantly." },
    { at: 16, label: "Map your controls", caption: "AI cross-maps every control and highlights the gaps blocking sign‑off." },
    { at: 27.5, label: "Magic Mapping", caption: "Drop in evidence — AI suggests the controls each file satisfies." },
    { at: 35.5, label: "Export audit-ready reports", caption: "One click: a branded PDF backed by a hash-chained audit trail." },
    { at: 50, label: "Isolated. Logged. Verifiable.", caption: "Isolated workspaces, every action logged, every export verifiable." },
    { at: 56, label: "Audit-ready. Always.", caption: "Create your secure workspace today." },
];

const WALKTHROUGH = [
    {
        title: "Connect",
        body: "Connect Okta, AWS, and GitHub, or upload files.",
    },
    {
        title: "Map",
        body: "AI matches your evidence to compliance controls across every framework.",
    },
    {
        title: "Export",
        body: "Send your auditor a clean, organized package and a branded PDF.",
    },
];

export const SeeItInAction = () => {
    const [step, setStep] = useState(0);

    return (
        <section id="how-it-works" className="max-w-7xl mx-auto px-5 sm:px-10 py-16 lg:py-24 border-t border-border/60 scroll-mt-20" data-testid="see-it-in-action">
            <div className="max-w-3xl mb-10">
                <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
                    How it works
                </h2>
            </div>
            <div role="tablist" aria-label="How it works steps" className="flex gap-2 overflow-x-auto scrollbar-none -mx-1 px-1">
                {WALKTHROUGH.map((s, i) => (
                    <button
                        key={s.title}
                        type="button"
                        role="tab"
                        aria-selected={i === step}
                        onClick={() => setStep(i)}
                        data-testid={`walkthrough-step-${i}`}
                        className={`shrink-0 whitespace-nowrap rounded-md border px-4 h-11 font-display text-sm font-medium transition-colors ${
                            i === step ? "border-foreground/50 bg-card" : "border-border/60 text-muted-foreground hover:bg-muted/40"
                        }`}
                    >
                        <span className="mr-1.5 text-muted-foreground" aria-hidden="true">{i + 1}</span>
                        {s.title}
                    </button>
                ))}
            </div>
            <div className="mt-4 grid" data-testid="walkthrough-mockup">
                {WALKTHROUGH.map((s, i) => (
                    <div
                        key={s.title}
                        role="tabpanel"
                        aria-hidden={i !== step}
                        className={`[grid-area:1/1] rounded-2xl border border-border bg-card p-6 sm:p-8 ${i === step ? "" : "invisible pointer-events-none"}`}
                    >
                        <p className="text-muted-foreground mb-6 max-w-2xl">{s.body}</p>
                        {i === 0 && (
                    <div className="space-y-3">
                        {["SOC 2", "ISO 27001", "HIPAA"].map((f, i) => (
                            <div key={f} className="rise-in flex items-center gap-3 rounded-xl border border-border bg-background/60 px-4 py-3" style={{ animationDelay: `${i * 300}ms` }}>
                                <Check className="h-4 w-4 text-accent shrink-0" strokeWidth={2.5} />
                                <span className="font-display font-semibold whitespace-nowrap">{f}</span>
                                <span className="ml-auto text-xs font-medium px-2 py-1 rounded-full border border-border bg-card whitespace-nowrap">Applied</span>
                            </div>
                        ))}
                    </div>
                )}
                {i === 1 && (
                    <div className="space-y-3">
                        {[
                            { c: "AC-01 · Access Control", maps: ["SOC 2 CC6.1", "ISO A.5.15"], gap: false },
                            { c: "DE-01 · Data Encryption", maps: ["SOC 2 CC6.7", "HIPAA §164.312"], gap: false },
                            { c: "IR-01 · Incident Response", maps: ["ISO A.5.24"], gap: true },
                        ].map((r, j) => (
                            <div key={r.c} className="rise-in flex flex-wrap items-center gap-2 rounded-xl border border-border bg-background/60 p-4" style={{ animationDelay: `${j * 300}ms` }}>
                                <span className="text-sm font-medium">{r.c}</span>
                                <span className="flex flex-wrap gap-1.5 ml-auto">
                                    {r.maps.map((m) => (
                                        <span key={m} className="mono-label px-2 py-0.5 rounded-full border border-border bg-card">{m}</span>
                                    ))}
                                    {r.gap && (
                                        <span className="mono-label px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-500">gap</span>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                {i === 2 && (
                    <div className="space-y-4">
                        <div className="rise-in flex items-center gap-3 rounded-xl border border-border bg-background/60 p-4">
                            <FileText className="h-5 w-5 shrink-0" strokeWidth={1.5} />
                            <span className="text-sm font-medium min-w-0 break-all">auditvanguard_soc2_report.pdf</span>
                            <span className="mono-label ml-auto text-muted-foreground hidden sm:inline">branded · versioned</span>
                        </div>
                        <div className="rise-in inline-flex items-center gap-2 rounded-md bg-accent text-accent-foreground px-5 h-11 font-display font-semibold text-sm" style={{ animationDelay: "350ms" }}>
                            <FileDown className="h-4 w-4" strokeWidth={1.5} /> Export PDF
                        </div>
                        <div className="rise-in flex items-center gap-2 text-sm text-muted-foreground" style={{ animationDelay: "700ms" }}>
                            <CircleCheck className="h-4 w-4 text-emerald-500 shrink-0" strokeWidth={1.5} />
                            <code className="mono-label">sha256 9f2c…e81a</code> hash-chained audit trail · verified
                        </div>
                    </div>
                )}
                    </div>
                ))}
            </div>
        </section>
    );
};
